import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { API_BASE_URL } from '../../config/api';
import Pagination from '../../components/Pagination/Pagination';
import AdBanner from '../../components/AdBanner/AdBanner';
import { productsApi } from '../../services/apiService';
import { useCurrency } from '../../context/CurrencyContext';
import './AllCategoriesPage.css';

const AllCategoriesPage = () => {
  const navigate = useNavigate();
  const { formatPriceRange } = useCurrency();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const categoriesPerPage = 24; // 4 cards per row × 6 rows = 24

  // Mobile state
  const [selectedMobileCat, setSelectedMobileCat] = useState('for-you');
  const [mobileProducts, setMobileProducts] = useState([]);
  const [mobileProductsLoading, setMobileProductsLoading] = useState(false);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Fetch mobile products when selected category changes
  useEffect(() => {
    const fetchMobileProds = async () => {
      setMobileProductsLoading(true);
      try {
        let prods = [];
        if (selectedMobileCat === 'for-you') {
          const res = await productsApi.getAll({ limit: 50 });
          prods = Array.isArray(res) ? res : (res.products || []);
        } else if (selectedMobileCat === 'featured') {
          prods = await productsApi.getFeatured();
        } else if (selectedMobileCat === 'deals') {
          prods = await productsApi.getPopular();
        } else {
          const res = await productsApi.getAll({ category: selectedMobileCat, limit: 50 });
          prods = Array.isArray(res) ? res : (res.products || []);
        }
        setMobileProducts(prods.filter(p => p.status === 'published'));
      } catch (err) {
        console.error('Mobile products error:', err);
      } finally {
        setMobileProductsLoading(false);
      }
    };
    fetchMobileProds();
  }, [selectedMobileCat]);

  const handleCategoryClick = (category) => {
    navigate(`/products?category=${category.id}`);
  };

  const handleSubcategoryClick = (category, subcategory) => {
    navigate(`/products?category=${category.id}&subcategory=${subcategory.slug}`);
  };

  // Default image for categories without images
  const defaultImage = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=300&fit=crop';

  // Handle image error - show default image on error
  const handleImageError = (e) => {
    e.target.onerror = null; // Prevent infinite loop
    e.target.src = defaultImage;
  };

  // Derived subcategories for mobile right panel
  const displaySubcategories = useMemo(() => {
    if (['for-you', 'featured', 'deals'].includes(selectedMobileCat)) {
      return categories
        .slice(0, 5)
        .flatMap(c => (c.subcategories || []).slice(0, 3))
        .slice(0, 9);
    }
    const cat = categories.find(c => String(c.id) === String(selectedMobileCat));
    return (cat?.subcategories || []).slice(0, 9);
  }, [selectedMobileCat, categories]);

  // Sidebar special items + dynamic categories
  const sidebarSpecial = [
    { id: 'for-you', label: 'For you' },
    { id: 'featured', label: 'Featured' },
    { id: 'deals', label: 'Deals' },
  ];

  // Pagination logic
  const totalPages = Math.ceil(categories.length / categoriesPerPage);
  const indexOfLastCategory = currentPage * categoriesPerPage;
  const indexOfFirstCategory = indexOfLastCategory - categoriesPerPage;
  const currentCategories = categories.slice(indexOfFirstCategory, indexOfLastCategory);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="all-categories-page">

      {/* ===== DESKTOP LAYOUT (hidden on mobile via CSS) ===== */}
      <div className="categories-desktop-only">
        <Header />
        
        <main className="categories-main-content">
          <div className="categories-container">
            {/* Breadcrumb */}
            <div className="categories-breadcrumb">
              <Link to="/" className="breadcrumb-link">Home</Link>
              <svg className="breadcrumb-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="breadcrumb-current">All Categories</span>
            </div>

            {/* Page Header */}
            <div className="categories-header">
              <h1 className="categories-title">All Categories</h1>
              <p className="categories-subtitle">Find your desired category and explore products and services.</p>
            </div>

            {/* Category Top Ad Banner */}
            <AdBanner placementSlug="category-log-top" className="ad-banner-fullwidth" />

            {/* Loading State */}
            {loading ? (
              <div className="loading-container">
                <p>Loading categories...</p>
              </div>
            ) : (
              <>
                {/* Categories Grid */}
                <div className="categories-grid-section">
                  <div className="categories-grid">
                    {currentCategories.map((category) => (
                      <div key={category.id} className="category-card" onClick={() => handleCategoryClick(category)}>
                        <div className="category-image-wrapper">
                          <img 
                            src={category.image || defaultImage} 
                            alt={category.name}
                            className="category-image"
                            onError={handleImageError}
                          />
                        </div>
                        <div className="category-info">
                          <h3 className="category-name">{category.name}</h3>
                          <div className="subcategories-list">
                            {category.subcategories && category.subcategories.slice(0, 4).map((subcategory) => (
                              <div 
                                key={subcategory.id || subcategory.name} 
                                className="subcategory-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSubcategoryClick(category, subcategory);
                                }}
                              >
                                <span className="subcategory-name">{subcategory.name || subcategory}</span>
                              </div>
                            ))}
                            <span 
                              className="view-all-link"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCategoryClick(category);
                              }}
                            >
                              View All ....
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pagination */}
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
              </>
            )}
          </div>
        </main>

        <Footer />
      </div>

      {/* ===== MOBILE LAYOUT - Alibaba style (shown only on mobile via CSS) ===== */}
      <div className="mob-cat-layout">
        {/* Mobile Header */}
        <div className="mob-cat-header">
          <h1 className="mob-cat-header-title">Categories</h1>
          <button className="mob-cat-header-help">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="1" width="22" height="22" rx="3" stroke="#333" strokeWidth="1.5" fill="none"/>
              <path d="M9 9a3 3 0 0 1 6 0c0 2-3 3-3 3" stroke="#333" strokeWidth="1.5" strokeLinecap="round"/>
              <circle cx="12" cy="17" r="0.5" fill="#333" stroke="#333" strokeWidth="1"/>
            </svg>
          </button>
        </div>

        {/* Body: Sidebar + Content */}
        <div className="mob-cat-body">

          {/* Left Sidebar */}
          <div className="mob-cat-sidebar">
            {sidebarSpecial.map(item => (
              <button
                key={item.id}
                className={`mob-cat-sidebar-item ${selectedMobileCat === item.id ? 'active' : ''}`}
                onClick={() => setSelectedMobileCat(item.id)}
              >
                {item.label}
              </button>
            ))}
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`mob-cat-sidebar-item ${String(selectedMobileCat) === String(cat.id) ? 'active' : ''}`}
                onClick={() => setSelectedMobileCat(cat.id)}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Right Content */}
          <div className="mob-cat-right">
            {/* Subcategories / Icon Grid */}
            {displaySubcategories.length > 0 && (
              <>
                <p className="mob-cat-section-title">Recommendations</p>
                <div className="mob-cat-subcat-grid">
                  {displaySubcategories.map((sub, idx) => {
                    const cat = categories.find(c =>
                      (c.subcategories || []).some(s => s.id === sub.id)
                    );
                    return (
                      <div
                        key={sub.id || idx}
                        className="mob-cat-subcat-item"
                        onClick={() => navigate(cat ? `/category/${cat.slug || cat.id}/${sub.slug}` : '/products')}
                      >
                        <div className="mob-cat-subcat-circle">
                          {sub.image ? (
                            <img src={sub.image} alt={sub.name} onError={handleImageError} />
                          ) : (
                            <span className="mob-cat-subcat-initial">{(sub.name || '?').charAt(0)}</span>
                          )}
                        </div>
                        <span className="mob-cat-subcat-name">{sub.name}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Products Section */}
            <p className="mob-cat-section-title" style={{ marginTop: displaySubcategories.length > 0 ? 16 : 0 }}>
              Get product inspiration
            </p>
            {mobileProductsLoading ? (
              <div className="mob-cat-loading">Loading products...</div>
            ) : mobileProducts.length === 0 ? (
              <div className="mob-cat-loading">No products found</div>
            ) : (
              <div className="mob-cat-prod-grid">
                {mobileProducts.map(product => (
                  <div
                    key={product.id}
                    className="mob-cat-prod-card"
                    onClick={() => navigate(`/product/${product.id}`)}
                  >
                    <div className="mob-cat-prod-img-wrap">
                      <img
                        src={product.image || defaultImage}
                        alt={product.name}
                        className="mob-cat-prod-img"
                        onError={handleImageError}
                      />
                      <button
                        className="mob-cat-prod-search-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/products?search=${encodeURIComponent(product.name)}`);
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <circle cx="11" cy="11" r="7" stroke="#555" strokeWidth="2" fill="none"/>
                          <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="#555" strokeWidth="2"/>
                        </svg>
                      </button>
                    </div>
                    <div className="mob-cat-prod-info">
                      <span className="mob-cat-prod-price">
                        {formatPriceRange(product.priceMin, product.priceMax)}
                      </span>
                      <span className="mob-cat-prod-moq">Min. order: {product.moq || '1 piece'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Bottom Navigation */}
        <div className="mob-cat-bnav">
          <div className="mob-cat-bnav-item" onClick={() => navigate('/')}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none"/>
              <polyline points="9 22 9 12 15 12 15 22" fill="none"/>
            </svg>
            <span>Home</span>
          </div>
          <div className="mob-cat-bnav-item active">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" fill="none"/>
              <rect x="14" y="3" width="7" height="7" fill="none"/>
              <rect x="3" y="14" width="7" height="7" fill="none"/>
              <rect x="14" y="14" width="7" height="7" fill="none"/>
            </svg>
            <span>Categories</span>
          </div>
          <div className="mob-cat-bnav-item" onClick={() => navigate('/mobile/messenger')}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" fill="none"/>
            </svg>
            <span>Messenger</span>
          </div>
          <div className="mob-cat-bnav-item" onClick={() => navigate('/mobile/cart')}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="21" r="1"/>
              <circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" fill="none"/>
            </svg>
            <span>Cart</span>
          </div>
          <div className="mob-cat-bnav-item" onClick={() => navigate('/mobile/alibaba')}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" fill="none"/>
              <circle cx="12" cy="7" r="4" fill="none"/>
            </svg>
            <span>My Alibaba</span>
          </div>
        </div>
      </div>

    </div>
  );
};

export default AllCategoriesPage;
